import { MyTaskListItem } from '../../services/myTasksApi';
import { formatDateBr } from '../../utils/formatDateBr';

export type MyTasksKanbanColumnId = 'Backlog' | 'ToDo' | 'Doing' | 'Done';

type ColumnConfig = {
  id: MyTasksKanbanColumnId;
  label: string;
  headerClass: string;
  statuses: string[];
};

const COLUMNS: ColumnConfig[] = [
  {
    id: 'Backlog',
    label: 'Backlog',
    headerClass: 'bg-slate-500 text-white',
    statuses: ['Planned', 'Cancelled'],
  },
  {
    id: 'ToDo',
    label: 'To Do',
    headerClass: 'bg-violet-600 text-white',
    statuses: ['Overdue'],
  },
  {
    id: 'Doing',
    label: 'Doing',
    headerClass: 'bg-sky-500 text-white',
    statuses: ['InProgress'],
  },
  {
    id: 'Done',
    label: 'Done',
    headerClass: 'bg-emerald-600 text-white',
    statuses: ['Completed'],
  },
];

type MyTasksKanbanBoardProps = {
  items: MyTaskListItem[];
  onSelect: (item: MyTaskListItem) => void;
  onMove: (item: MyTaskListItem, column: MyTasksKanbanColumnId) => void;
  onCreate?: () => void;
  movingId?: string | null;
};

function initials(name?: string | null): string {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function daysHoursMinutesBetween(from: Date, to: Date): string {
  const ms = Math.max(0, to.getTime() - from.getTime());
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  return `${days} Dias ${hours} Horas ${minutes} Minutos`;
}

function scheduleMeta(item: MyTaskListItem): {
  late: boolean;
  label: string;
  detail: string | null;
  progress: number;
} {
  const now = new Date();
  const end = item.plannedEndDate ? new Date(`${item.plannedEndDate}T23:59:59`) : null;
  const start = item.plannedStartDate
    ? new Date(`${item.plannedStartDate}T00:00:00`)
    : end
      ? new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000)
      : null;

  if (!end) {
    return { late: false, label: 'Em Dia', detail: null, progress: item.status === 'Completed' ? 100 : 35 };
  }

  const late = item.status === 'Overdue' || (item.status !== 'Completed' && now > end);
  if (late) {
    return {
      late: true,
      label: 'Atrasado',
      detail: `Atraso: ${daysHoursMinutesBetween(end, now)}`,
      progress: 100,
    };
  }

  if (item.status === 'Completed') {
    return { late: false, label: 'Em Dia', detail: null, progress: 100 };
  }

  let progress = 40;
  if (start && end.getTime() > start.getTime()) {
    progress = Math.min(95, Math.max(8, ((now.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100));
  }

  return {
    late: false,
    label: 'Em Dia',
    detail: `Restante: ${daysHoursMinutesBetween(now, end)}`,
    progress,
  };
}

function priorityTag(priority?: string | null): { label: string; className: string } | null {
  switch (priority) {
    case 'Critical':
    case 'High':
      return { label: 'Urgente', className: 'bg-rose-100 text-rose-700' };
    case 'Low':
      return { label: 'Baixa', className: 'bg-slate-100 text-slate-600' };
    default:
      return null;
  }
}

function kindTag(kind?: string | null): { label: string; className: string } | null {
  if (kind === 'Meta') return { label: 'Meta', className: 'bg-sky-100 text-sky-700' };
  if (kind === 'Marco') return { label: 'Marco', className: 'bg-amber-100 text-amber-800' };
  if (kind === 'Tarefa' || !kind) return { label: 'Tarefa', className: 'bg-violet-100 text-violet-700' };
  return { label: kind, className: 'bg-slate-100 text-slate-600' };
}

export function columnForStatus(status: string): MyTasksKanbanColumnId {
  if (status === 'InProgress') return 'Doing';
  if (status === 'Completed') return 'Done';
  if (status === 'Overdue') return 'ToDo';
  return 'Backlog';
}

export function MyTasksKanbanBoard({
  items,
  onSelect,
  onMove,
  onCreate,
  movingId,
}: MyTasksKanbanBoardProps) {
  function handleDrop(column: MyTasksKanbanColumnId, event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    const taskId = event.dataTransfer.getData('text/task-id');
    if (!taskId) return;
    const item = items.find((entry) => entry.id === taskId);
    if (!item) return;
    if (columnForStatus(item.status) === column) return;
    onMove(item, column);
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {COLUMNS.map((column) => {
        const columnItems = items.filter((item) => column.statuses.includes(item.status));

        return (
          <div
            key={column.id}
            className="flex min-w-[280px] max-w-[320px] flex-1 flex-col rounded-lg bg-secondary-100/80"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => handleDrop(column.id, event)}
          >
            <div className={`flex items-center justify-between rounded-t-lg px-3 py-2 ${column.headerClass}`}>
              <span className="text-sm font-semibold tracking-wide">{column.label}</span>
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-black/80 px-1.5 text-xs font-semibold text-white">
                {columnItems.length}
              </span>
            </div>

            <div className="flex min-h-[160px] flex-1 flex-col gap-2 p-2">
              {columnItems.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-secondary-500">Nenhum item</p>
              ) : (
                columnItems.map((item) => {
                  const schedule = scheduleMeta(item);
                  const urgent = priorityTag(item.priority);
                  const kind = kindTag(item.kind);
                  const done = item.status === 'Completed';
                  const busy = movingId === item.id;
                  const unassigned = Boolean(item.isUnassigned) || !item.ownerUserName?.trim();

                  return (
                    <button
                      key={item.id}
                      type="button"
                      draggable={!busy}
                      disabled={busy}
                      onDragStart={(event) => {
                        event.dataTransfer.setData('text/task-id', item.id);
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => onSelect(item)}
                      className="w-full rounded-md border border-secondary-200 bg-white p-3 text-left shadow-sm transition hover:shadow-md disabled:opacity-60"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={`line-clamp-2 text-sm font-semibold text-secondary-900 ${
                            done ? 'line-through text-secondary-500' : ''
                          }`}
                        >
                          {item.title}
                        </p>
                        <span
                          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                            unassigned ? 'bg-amber-500' : 'bg-secondary-800'
                          }`}
                          title={unassigned ? 'Não atribuído' : (item.ownerUserName ?? undefined)}
                        >
                          {unassigned ? '—' : initials(item.ownerUserName)}
                        </span>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {kind ? (
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${kind.className}`}>
                            {kind.label}
                          </span>
                        ) : null}
                        {urgent ? (
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${urgent.className}`}>
                            {urgent.label}
                          </span>
                        ) : null}
                        {unassigned ? (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                            Não atribuído
                          </span>
                        ) : null}
                        {item.isOrphan ? (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                            Órfã
                          </span>
                        ) : null}
                      </div>

                      {schedule.detail ? (
                        <p className={`mt-2 text-[11px] ${schedule.late ? 'text-rose-600' : 'text-secondary-600'}`}>
                          {schedule.detail}
                        </p>
                      ) : item.plannedEndDate ? (
                        <p className="mt-2 text-[11px] text-secondary-500">
                          Prazo: {formatDateBr(item.plannedEndDate)}
                        </p>
                      ) : null}

                      <div className="mt-2 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary-200">
                          <div
                            className={`h-full rounded-full ${schedule.late ? 'bg-rose-500' : 'bg-emerald-500'}`}
                            style={{ width: `${schedule.progress}%` }}
                          />
                        </div>
                        <span
                          className={`shrink-0 text-[11px] font-semibold ${
                            schedule.late ? 'text-rose-600' : 'text-emerald-600'
                          }`}
                        >
                          {schedule.label}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}

              {onCreate && column.id !== 'Done' ? (
                <button
                  type="button"
                  className="mt-auto px-2 py-2 text-left text-xs font-medium text-secondary-500 hover:text-secondary-800"
                  onClick={onCreate}
                >
                  Criar Tarefa...
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
